import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { Theme } from "@mui/material/styles";
import { ZoneMapDialog } from "@curbsuite/components/permits";
import {
  GET_PERMIT_TYPES_FOR_PAYEE,
  GetPermitTypesForPayeeData,
  GetPermitTypesForPayeeVars,
  GetPermitTypesPermitTypeRaw,
  transformPermitType,
} from "common/graphql/queries";
import { PermitTypeCard, PermitTypeCardSkeleton } from "./index";
import { PermitsEmptyState } from "../components";
import type { PermitTypeWithZones } from "./PermitTypeCard";
import { useUserProfile } from "@app.automotus.io/components/hooks";

export const PermitTypes: React.FC = () => {
  const [selectedPermitType, setSelectedPermitType] = useState<PermitTypeWithZones | null>(null);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);

  const { userProfile } = useUserProfile();

  const { data, loading, error } = useQuery<GetPermitTypesForPayeeData, GetPermitTypesForPayeeVars>(
    GET_PERMIT_TYPES_FOR_PAYEE,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        payeeAccountId: userProfile?.account.id || "",
      },
      skip: !userProfile,
    },
  );

  const permitTypes = (data?.permitTypes || []).map((raw: GetPermitTypesPermitTypeRaw) => transformPermitType(raw));

  const handleViewZones = (permitType: PermitTypeWithZones) => {
    setSelectedPermitType(permitType);
    setZoneDialogOpen(true);
  };

  const handleCloseZoneDialog = () => {
    setZoneDialogOpen(false);
    setSelectedPermitType(null);
  };

  const renderContent = () => {
    if (loading && !data) {
      return (
        <Stack spacing={3}>
          {[1, 2, 3].map((i) => (
            <PermitTypeCardSkeleton key={i} />
          ))}
        </Stack>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load permit types. Please try again later.
        </Alert>
      );
    }

    if (permitTypes.length === 0) {
      return <PermitsEmptyState />;
    }

    return (
      <Stack spacing={3}>
        {permitTypes.map((permitType) => (
          <PermitTypeCard key={permitType.id} permitType={permitType} onViewZones={handleViewZones} />
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%", flexGrow: 1 }}>
      {/* Main Content */}
      {/* Header */}
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 3 }}>
        Permit Types
      </Typography>

      {/* Info Banner — only shown when permit types are configured */}
      {permitTypes.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 2,
            mb: 4,
            bgcolor: (theme: Theme) => theme.palette.grey[100],
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <LockOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="body2">
            Permit types are configured by Automotus. Cities have read-only access in Phase 1.
          </Typography>
        </Box>
      )}

      {/* Permit Type Cards */}
      {renderContent()}

      {/* Zone Map Dialog */}
      <ZoneMapDialog
        open={zoneDialogOpen}
        onClose={handleCloseZoneDialog}
        title={selectedPermitType ? `${selectedPermitType.name} - Zones` : "Permit Zones"}
        zones={selectedPermitType?.zones || []}
      />
    </Box>
  );
};

export default PermitTypes;
